import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type {
  CreateUserRequest,
  UpdateUserRequest,
  UserResponse,
} from '../../schema/data-contracts';
import {
  GetCurrentUserLatestEstimationQueryKey,
  type MutationProps,
  type UsersListParams,
} from '../types';

export const createGetUserQueryKey = (userId: string) => ['user', userId] as const;

export const useUserQueryActions = () => {
  const { auth } = useAuth();
  const {
    userController: {
      v1UsersList,
      v1UsersCreate,
      v1EstimationsUsersList,
      v1UsersDelete,
      v1UsersUpdate,
      v1UsersCurrentList,
      v1UsersCurrentLatestActiveEstimationList,
      v1UsersDetail,
    },
  } = useApi();

  const queryClient = useQueryClient();

  const useGetCurrentUser = () =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['user', 'current'],
      queryFn: async () => {
        const { data } = await v1UsersCurrentList();
        return data;
      },
    });

  const useGetCurrentUserLatestActiveEstimation = () =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: GetCurrentUserLatestEstimationQueryKey,
      queryFn: async () => {
        const { data } = await v1UsersCurrentLatestActiveEstimationList();
        return data;
      },
    });

  const getUser = (userId: string) =>
    queryClient.fetchQuery({
      queryKey: createGetUserQueryKey(userId),
      queryFn: async () => {
        const { data } = await v1UsersDetail(userId);
        return data;
      },
    });

  const useGetUser = (userId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && !!userId,
      queryKey: createGetUserQueryKey(userId!),
      queryFn: async () => {
        const { data } = await v1UsersDetail(userId!);
        return data;
      },
    });

  const useGetUsers = (params?: Parameters<typeof v1UsersList>[0], predicate?: boolean) =>
    useQuery({
      enabled: Boolean(auth) && predicate,
      queryKey: ['users', params],
      queryFn: async () => {
        const {
          data: { items },
        } = await v1UsersList(params);

        return items;
      },
    });

  const useGetUsersPaged = (params?: Parameters<typeof v1UsersList>[0]) =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['users-paged', params],
      queryFn: async () => {
        const { data } = await v1UsersList(params);
        return data as { totalCount: number; items: UserResponse[] };
      },
    });

  const useGetUsersByEstimationId = (estimationId: string | undefined, params?: UsersListParams) =>
    useQuery({
      enabled: Boolean(auth) && Boolean(estimationId),
      queryKey: ['estimation-users', estimationId, params],
      queryFn: async () => {
        const {
          data: { items },
        } = await v1EstimationsUsersList(estimationId!, params);
        return items;
      },
    });

  const useCreateUser = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['create-user'],
      mutationFn: async (user: CreateUserRequest) => {
        const { data } = await v1UsersCreate(user);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['users'] });
      },
    });

  const useUpdateUser = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['update-user'],
      mutationFn: async ({ userId, userData }: { userId: string; userData: UpdateUserRequest }) => {
        const { data } = await v1UsersUpdate(userId, userData);
        return data;
      },
      onSuccess: async (_, { userId }) => {
        await queryClient.invalidateQueries({ queryKey: ['users'] });
        await queryClient.invalidateQueries({ queryKey: createGetUserQueryKey(userId) });
      },
    });

  const useDeleteUser = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['delete-user'],
      mutationFn: async (userId: string) => {
        const { data } = await v1UsersDelete(userId);
        return data;
      },
      onSuccess: async (_, userId) => {
        await queryClient.invalidateQueries({ queryKey: ['users'] });
        await queryClient.invalidateQueries({ queryKey: createGetUserQueryKey(userId) });
      },
    });

  return {
    useGetUsers,
    useGetUsersPaged,
    useCreateUser,
    useGetUsersByEstimationId,
    useUpdateUser,
    useDeleteUser,
    useGetCurrentUser,
    useGetCurrentUserLatestActiveEstimation,
    useGetUser,
    getUser,
  };
};
